import { useAuth, useClerk } from "@clerk/tanstack-react-start";
import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import { LogOutIcon } from "lucide-react";
import type { NavbarViewer } from "~/routes/__root";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export function ProfileDropdown({
  initialViewer,
}: {
  initialViewer: NavbarViewer;
}) {
  const { isLoaded, isSignedIn } = useAuth();
  const { user, signOut } = useClerk();
  const navigate = useNavigate();
  const router = useRouter();

  if (isLoaded && !isSignedIn) {
    return (
      <Button variant="ghost" size="sm" asChild>
        <Link to="/sign-in">Sign In</Link>
      </Button>
    );
  }

  const displayImageUrl =
    user?.imageUrl ?? initialViewer?.imageUrl ?? undefined;
  const displayName =
    user?.fullName ?? user?.firstName ?? initialViewer?.fullName ?? "Overseer";
  const avatar = (
    <Avatar>
      <AvatarImage src={displayImageUrl} alt={displayName} />
      <AvatarFallback>{displayName.charAt(0)}</AvatarFallback>
    </Avatar>
  );

  if (!isLoaded || !isSignedIn || !user) {
    if (initialViewer) {
      return <div className="size-8">{avatar}</div>;
    }

    return (
      <Button variant="ghost" size="sm" asChild>
        <Link to="/sign-in">Sign In</Link>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="rounded-full cursor-pointer outline outline-border/60 hover:outline-border/80 hover:bg-border/20">
        {avatar}
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuGroup>
          <DropdownMenuLabel>{user.fullName}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            render={<Link to="/dashboard" />}
          >
            Dashboard
          </DropdownMenuItem>
          {/* <DropdownMenuItem className={"cursor-pointer"}>
            Settings
          </DropdownMenuItem> */}
          <DropdownMenuItem
            className="cursor-pointer"
            render={<Link to="/dashboard/subscriptions" />}
          >
            Subscriptions
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className={"cursor-pointer"}
            variant="destructive"
            onClick={async () => {
              await signOut();
              await router.invalidate();
              await navigate({ to: "/" });
            }}
          >
            <LogOutIcon />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
